// File: src/lib/media/ingest/ingestFromLocalLibrary.ts
//
// Phase M1 (STRICT): Ingest a provided File into registry
// ------------------------------------------------------------------------------
// Strict-mode rules:
// - Picker + previewUrl + timeline:add are owned by ImportButton (UI authority).
// - This module does ONLY:
//   1) infer kind + duration (best-effort)
//   2) upload to Firebase Storage
//   3) write Firestore mediaRegistry/<key>
//   4) return canonical metadata (downloadUrl may be null if upload fails)
//
// No reducer drift. No event emission. No hidden DOM.

import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

import { getDbOrThrow, getStorageOrThrow } from "@/lib/firebase";

export type IngestResult = {
  key: string; // registry key = mediaId
  kind: "audio" | "video" | "image";
  storagePath: string; // canonical path (and compat)
  downloadUrl: string | null; // durable URL if upload succeeded
  durSec: number | null;
  fileName: string;
  mime: string;
};

function sanitizeKey(s: string): string {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_\-\.]/g, "")
    .slice(0, 96);
}

function inferKindFromFile(f: File): "audio" | "video" | "image" {
  const t = String(f.type || "").toLowerCase();
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("image/")) return "image";
  return "video";
}

function makeRequestId(): string {
  return (
    (globalThis.crypto?.randomUUID?.() as string | undefined) ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

async function getMediaDurationSec(file: File, kind: "audio" | "video" | "image"): Promise<number | null> {
  if (kind === "image") return null;

  // Best-effort metadata read: creates a temp object URL internally and revokes it.
  const url = URL.createObjectURL(file);
  try {
    const el = document.createElement(kind === "audio" ? "audio" : "video");
    el.preload = "metadata";
    el.src = url;

    const dur = await new Promise<number>((resolve, reject) => {
      const onLoaded = () => resolve(Number(el.duration));
      const onErr = () => reject(new Error("Failed to read media metadata duration"));
      el.addEventListener("loadedmetadata", onLoaded, { once: true });
      el.addEventListener("error", onErr, { once: true });
    });

    if (!Number.isFinite(dur) || dur <= 0) return null;
    return dur;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function setLastIngest(patch: Record<string, unknown>) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    w.__oc_media = w.__oc_media || {};
    const prev = w.__oc_media.__lastIngest || { at: Date.now() };
    w.__oc_media.__lastIngest = { ...prev, ...patch, at: Date.now() };
  } catch {
    // ignore
  }
}

export async function ingestFileToRegistryAndEmitAdd(opts: {
  file: File; // ✅ REQUIRED in strict mode
  storagePrefix?: string; // e.g. "omnicine/library"
  preferredKey?: string; // optional registry key override
  requestId?: string; // correlation (from ImportButton)
}): Promise<IngestResult> {
  const db = getDbOrThrow();
  const storage = getStorageOrThrow();
  const file = opts.file;
  const kind = inferKindFromFile(file);

  const requestId = (opts.requestId && String(opts.requestId).trim()) || makeRequestId();

  setLastIngest({
    ok: false,
    stage: "start",
    note: "Strict ingest started",
    requestId,
    fileName: file.name,
    mime: file.type || "",
    size: file.size,
    kind,
  });

  // 1) Duration (best-effort; never blocks ingest if it fails)
  const durSec = await getMediaDurationSec(file, kind).catch(() => null);

  // 2) Determine key + storage path
  const baseName = (file.name || "asset").replace(/\.[^.]+$/, "");
  const keyRaw = opts.preferredKey || `asset_${kind}_${baseName}_${Date.now()}`;
  const key = sanitizeKey(keyRaw);

  const ext = (file.name?.split(".").pop() || "").toLowerCase();
  const safeExt = ext && ext.length <= 8 ? ext : kind === "audio" ? "mp3" : kind === "image" ? "png" : "mp4";

  const prefix = (opts.storagePrefix || "omnicine/library")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  const storagePath = `${prefix}/${key}.${safeExt}`;

  setLastIngest({
    stage: "uploading",
    note: "Uploading to Storage…",
    requestId,
    key,
    storagePath,
    durSec,
  });

  // 3) Upload + get downloadUrl
  let downloadUrl: string | null = null;

  try {
    const objRef = storageRef(storage, storagePath);

    await uploadBytes(objRef, file, {
      contentType: file.type || undefined,
      cacheControl: "public, max-age=31536000",
    });

    setLastIngest({
      stage: "uploaded",
      note: "Uploaded to Storage",
      requestId,
    });

    downloadUrl = await getDownloadURL(objRef);

    setLastIngest({
      stage: "registry_write",
      note: "Writing Firestore registry doc…",
      requestId,
      downloadUrl,
    });

    // 4) Registry write (canonical payload)
    await setDoc(
      doc(db, "mediaRegistry", key),
      {
        v: 1,
        provider: "firebase",
        kind,
        path: storagePath, // ✅ canonical
        storagePath, // ✅ compat
        fileName: file.name,
        mime: file.type || "",
        durSec: durSec ?? null,
        url: downloadUrl, // optional fast resolve
        updatedAt: serverTimestamp(),
        source: "omnicine.ingest.localLibrary",
      },
      { merge: true },
    );

    setLastIngest({
      ok: true,
      stage: "done",
      note: "Strict ingest complete",
      requestId,
    });

    return {
      key,
      kind,
      storagePath,
      downloadUrl,
      durSec: durSec ?? null,
      fileName: file.name,
      mime: file.type || "",
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);

    // Write a registry doc anyway (optional) is *not* safe if upload failed,
    // but we still record a minimal entry so UI can resolve key->missing and retry later.
    try {
      await setDoc(
        doc(db, "mediaRegistry", key),
        {
          v: 1,
          provider: "firebase",
          kind,
          path: storagePath,
          storagePath,
          fileName: file.name,
          mime: file.type || "",
          durSec: durSec ?? null,
          url: null,
          updatedAt: serverTimestamp(),
          source: "omnicine.ingest.localLibrary",
          ingestError: msg,
        },
        { merge: true },
      );
    } catch {
      // ignore secondary failure
    }

    setLastIngest({
      ok: false,
      stage: "error",
      note: "Strict ingest failed",
      requestId,
      error: msg,
    });

    // Bubble up so ImportButton can mark failure but keep preview playback alive.
    throw err;
  }
}

// DEV debug hook (optional)
if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__oc_ingestLocal = ingestFileToRegistryAndEmitAdd;
}
